import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const ApplyPage = () => {
  const [advert, setAdvert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [formData, setFormData] = useState({
    quotePrice: '',
    estimatedDeliveryDays: '',
    notes: '',
    invoiceFile: null,
    collectionDateTime: '',
    deliveryDateTime: '',
  });
  const navigate = useNavigate();
  const location = useLocation();
  const advertId = location.state?.advertId;
  const isCompanyAuthenticated = localStorage.getItem('companyAuthenticated') === 'true';
  const companyId = localStorage.getItem('companyId');

  // Log initial localStorage state for debugging
  useEffect(() => {
    console.log('Initial localStorage check:', {
      companyAuthenticated: localStorage.getItem('companyAuthenticated'),
      companyId: localStorage.getItem('companyId'),
      advertId,
    });
  }, []);

  // Fetch advert details and check application status
  useEffect(() => {
    const fetchAdvert = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('Fetching advert details for advertId:', advertId);
        const advertResponse = await fetch(`https://dropquote.co.za/backend/get_advert.php?id=${advertId}`, {
          headers: { 'Content-Type': 'application/json' },
        });
        if (!advertResponse.ok) {
          throw new Error(`HTTP error! Status: ${advertResponse.status}, StatusText: ${advertResponse.statusText}`);
        }
        const advertResult = await advertResponse.json();
        if (advertResult.success) {
          setAdvert(advertResult.data);
          // Pre-fill collection and delivery date/time with advert defaults if available
          setFormData((prev) => ({
            ...prev,
            collectionDateTime: advertResult.data.collection_date ? new Date(advertResult.data.collection_date).toISOString().slice(0, 16) : '',
            deliveryDateTime: advertResult.data.delivery_date ? new Date(advertResult.data.delivery_date).toISOString().slice(0, 16) : '',
          }));
        } else {
          setError(advertResult.message || 'Failed to fetch advert details');
          return;
        }

        console.log('Checking application status for advertId:', advertId, 'companyId:', companyId);
        const appliedResponse = await fetch(
          `https://dropquote.co.za/backend/check_application.php?advertId=${advertId}&companyId=${companyId}`,
          {
            headers: { 'Content-Type': 'application/json' },
          }
        );
        if (!appliedResponse.ok) {
          throw new Error(`HTTP error! Status: ${appliedResponse.status}, StatusText: ${appliedResponse.statusText}`);
        }
        const appliedResult = await appliedResponse.json();
        if (appliedResult.success) {
          setHasApplied(appliedResult.hasApplied);
        } else {
          setError(appliedResult.message || 'Failed to check application status');
        }
      } catch (error) {
        console.error('Fetch error:', error);
        setError(`Error fetching advert: ${error.message.includes('Failed to fetch') ? 'Failed to connect to server. Check CORS or server status.' : error.message}`);
      } finally {
        setLoading(false);
      }
    };

    // Validate all required conditions
    const parsedCompanyId = parseInt(companyId, 10);
    if (!isCompanyAuthenticated || !companyId || isNaN(parsedCompanyId) || parsedCompanyId <= 0 || !advertId) {
      console.log('Redirecting due to missing or invalid data:', {
        isCompanyAuthenticated,
        companyId,
        parsedCompanyId,
        advertId,
      });
      navigate('/company-auth', {
        state: {
          advertId,
          error: !isCompanyAuthenticated
            ? 'Please log in to apply.'
            : !companyId || isNaN(parsedCompanyId) || parsedCompanyId <= 0
            ? 'Invalid or missing Company ID. Please log in again.'
            : 'Advert ID missing.',
        },
      });
      return;
    }

    fetchAdvert();
  }, [isCompanyAuthenticated, companyId, advertId, navigate]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'invoiceFile') {
      setFormData({ ...formData, invoiceFile: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate collection and delivery date/time
    if (!formData.collectionDateTime || !formData.deliveryDateTime) {
      setError('Please specify both collection and delivery date/time.');
      return;
    }

    const collectionDateTime = new Date(formData.collectionDateTime);
    const deliveryDateTime = new Date(formData.deliveryDateTime);
    if (deliveryDateTime <= collectionDateTime) {
      setError('Delivery date/time must be after collection date/time.');
      return;
    }

    const data = new FormData();
    data.append('advertId', advertId);
    data.append('companyId', companyId);
    data.append('quotePrice', formData.quotePrice);
    data.append('estimatedDeliveryDays', formData.estimatedDeliveryDays);
    data.append('notes', formData.notes);
    data.append('collectionDateTime', formData.collectionDateTime);
    data.append('deliveryDateTime', formData.deliveryDateTime);
    if (formData.invoiceFile) {
      data.append('invoiceFile', formData.invoiceFile);
    }

    try {
      console.log('Submitting application for advertId:', advertId, 'companyId:', companyId);
      const response = await fetch('https://dropquote.co.za/backend/apply_advert.php', {
        method: 'POST',
        body: data,
      });

      const text = await response.text();
      console.log('Apply response status:', response.status, 'Raw response:', text);

      if (!response.ok) {
        throw new Error(`Server error: ${response.status} - ${text}`);
      }

      let result;
      try {
        result = JSON.parse(text);
      } catch (jsonError) {
        throw new Error(`Invalid JSON response: ${text}`);
      }

      if (result.success) {
        alert('Application submitted successfully! A confirmation email has been sent.');
        setHasApplied(true);
        navigate('/adverts');
      } else {
        setError(result.message || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setError(`Error submitting application: ${error.message.includes('Failed to fetch') ? 'Failed to connect to server. Check CORS or server status.' : error.message}`);
    }
  };

  return (
    <div className="container my-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card shadow-lg border-dark">
            <div className="card-header bg-dark text-white">
              <h2 className="card-title mb-0 fs-4">Apply for Advert #{advertId || 'N/A'}</h2>
            </div>
            <div className="card-body p-4 bg-white">
              {loading && (
                <div className="text-center py-4">
                  <div className="spinner-border text-danger" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted">Fetching advert details...</p>
                </div>
              )}

              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError(null)}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              {!loading && advert && !error && (
                <>
                  <div className="mb-5">
                    <h3 className="fs-5 fw-bold mb-3 text-danger">Advert Details</h3>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <p className="mb-1"><strong>Package Type:</strong> {advert.package_type || 'N/A'}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1"><strong>Collection Point:</strong> {advert.collection_point || 'N/A'}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1"><strong>Delivery/Drop-off Point:</strong> {advert.drop_off_point || 'N/A'}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1"><strong>Number of Parcels:</strong> {advert.number_of_parcels || 'N/A'}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1"><strong>Number of Pallets:</strong> {advert.number_of_pallets || 'N/A'}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1">
                          <strong>Parcel Weight:</strong>{' '}
                          {advert.total_weight ? `${advert.total_weight} KG` : advert.volumes_in_tubes ? `${advert.volumes_in_tubes} m³` : 'N/A'}
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1"><strong>Quote By/Closing Date:</strong> {advert.closing_date || 'N/A'}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1"><strong>Created Date:</strong> {advert.created_date || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {hasApplied ? (
                    <div className="alert alert-dark" role="alert">
                      You have already applied for this advert.
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} encType="multipart/form-data">
                      <div className="mb-4">
                        <label htmlFor="collectionDateTime" className="form-label fw-bold">
                          Proposed Collection Date & Time
                        </label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          id="collectionDateTime"
                          name="collectionDateTime"
                          value={formData.collectionDateTime}
                          onChange={handleChange}
                          min={new Date().toISOString().slice(0, 16)}
                          required
                        />
                        {advert.collection_date && (
                          <small className="form-text text-muted">
                            Suggested: {new Date(advert.collection_date).toLocaleString()}
                          </small>
                        )}
                      </div>
                      <div className="mb-4">
                        <label htmlFor="deliveryDateTime" className="form-label fw-bold">
                          Proposed Delivery Date & Time
                        </label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          id="deliveryDateTime"
                          name="deliveryDateTime"
                          value={formData.deliveryDateTime}
                          onChange={handleChange}
                          min={formData.collectionDateTime || new Date().toISOString().slice(0, 16)}
                          required
                        />
                        {advert.delivery_date && (
                          <small className="form-text text-muted">
                            Suggested: {new Date(advert.delivery_date).toLocaleString()}
                          </small>
                        )}
                      </div>
                      <div className="mb-4">
                        <label htmlFor="quotePrice" className="form-label fw-bold">
                          Quote Price (R)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="quotePrice"
                          name="quotePrice"
                          value={formData.quotePrice}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                          required
                          placeholder="Enter your quoted price"
                        />
                      </div>
                      <div className="mb-4">
                        <label htmlFor="estimatedDeliveryDays" className="form-label fw-bold">
                          Estimated Delivery Time (Days)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="estimatedDeliveryDays"
                          name="estimatedDeliveryDays"
                          value={formData.estimatedDeliveryDays}
                          onChange={handleChange}
                          min="1"
                          required
                          placeholder="Enter estimated days"
                        />
                      </div>
                      <div className="mb-4">
                        <label htmlFor="notes" className="form-label fw-bold">
                          Additional Notes (Optional)
                        </label>
                        <textarea
                          className="form-control"
                          id="notes"
                          name="notes"
                          value={formData.notes}
                          onChange={handleChange}
                          rows="4"
                          placeholder="Any additional information or conditions"
                        />
                      </div>
                      <div className="mb-4">
                        <label htmlFor="invoiceFile" className="form-label fw-bold">
                          Quotation Invoice (Optional, PDF only)
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          id="invoiceFile"
                          name="invoiceFile"
                          accept="application/pdf"
                          onChange={handleChange}
                        />
                        {formData.invoiceFile && (
                          <small className="form-text text-muted mt-1">
                            Selected file: {formData.invoiceFile.name}
                          </small>
                        )}
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-danger px-4"
                        >
                          Submit Application
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-dark px-4"
                          onClick={() => navigate('/adverts')}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyPage;